import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";

const fmt = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SECTIONS = [["asset", "Aktiven"], ["liability", "Passiven"], ["revenue", "Ertrag"], ["expense", "Aufwand"]];

const CapitalTable = ({ capital }) => (
  <div className="overflow-x-auto border rounded-lg" data-testid="holding-capital-table">
    <Table>
      <TableHeader><TableRow>
        <TableHead>Tochter</TableHead><TableHead className="text-right">Quote</TableHead><TableHead className="text-right">Buchwert Beteiligung</TableHead>
        <TableHead className="text-right">Eigenkapital Tochter</TableHead><TableHead className="text-right">Goodwill</TableHead><TableHead className="text-right">Badwill</TableHead><TableHead className="text-right">Minderheiten</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {capital.map((c) => (
          <TableRow key={c.child_id} data-testid={`holding-capital-${c.child_id}`}>
            <TableCell className="text-sm">{c.child} <span className="text-xs text-slate-400">← {c.parent} · Kto {c.participation_account}</span></TableCell>
            <TableCell className="text-right font-mono text-sm">{c.participation_pct} %</TableCell>
            <TableCell className="text-right font-mono text-sm">{fmt(c.book_value)}</TableCell>
            <TableCell className="text-right font-mono text-sm">{fmt(c.equity)}</TableCell>
            <TableCell className="text-right font-mono text-sm">{c.goodwill ? fmt(c.goodwill) : "—"}</TableCell>
            <TableCell className="text-right font-mono text-sm">{c.badwill ? fmt(c.badwill) : "—"}</TableCell>
            <TableCell className="text-right font-mono text-sm">{c.minority_interest ? fmt(c.minority_interest) : "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const ConsolidationCard = ({ companyId, year }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLog, setShowLog] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setReport((await apiClient.get(`/companies/${companyId}/holding/consolidated`, { params: { year } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Konsolidierung fehlgeschlagen"); }
    finally { setLoading(false); }
  };

  const kpis = report ? [
    ["Aktiven", report.totals.assets], ["Passiven", report.totals.liabilities_equity], ["Ertrag", report.totals.income],
    ["Aufwand", report.totals.expense], ["Konzerngewinn", report.totals.profit],
    ["davon Minderheiten", report.totals.minority_profit], ["Anteil Konzern", report.totals.group_profit],
    ["Goodwill", report.totals.goodwill],
  ] : [];

  return (
    <Card className="p-4" data-testid="holding-consolidated-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h2 className="font-semibold text-slate-900">Konsolidierte Bilanz &amp; Erfolgsrechnung {year}</h2>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="holding-load-report-btn">{loading ? "Lade…" : "Berechnen"}</Button>
      </div>
      {!report && <div className="text-sm text-slate-400 py-6 text-center">«Berechnen» klicken — konsolidiert den gesamten Konzern inkl. Eliminierungen und Kapitalkonsolidierung.</div>}
      {report && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {kpis.map(([label, val]) => (
              <div key={label} className="rounded-lg border border-slate-200 p-2.5" data-testid={`holding-kpi-${label}`}>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
                <div className={`font-mono font-semibold tabular-nums text-sm ${label.includes("gewinn") || label === "Anteil Konzern" ? (val >= 0 ? "text-emerald-700" : "text-rose-700") : "text-slate-900"}`}>{fmt(val)}</div>
              </div>
            ))}
          </div>
          {report.warnings?.length > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 space-y-1" data-testid="holding-elimination-note">
              {report.warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}
            </div>
          )}
          {report.capital?.length > 0 && (
            <div>
              <div className="text-sm font-medium text-slate-800 mb-1">Kapitalkonsolidierung</div>
              <CapitalTable capital={report.capital} />
            </div>
          )}
          <button type="button" className="text-xs text-violet-700 flex items-center gap-1" onClick={() => setShowLog(!showLog)} data-testid="holding-elim-log-toggle">
            {showLog ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Konsolidierungsbuchungen ({report.eliminations.length})
          </button>
          {showLog && (
            <div className="border rounded-lg overflow-x-auto" data-testid="holding-elim-log">
              <Table>
                <TableHeader><TableRow><TableHead>Art</TableHead><TableHead>Konto</TableHead><TableHead>Firma</TableHead><TableHead>Text</TableHead><TableHead className="text-right">Betrag (S−H)</TableHead></TableRow></TableHeader>
                <TableBody>
                  {report.eliminations.map((e, i) => (
                    <TableRow key={i}><TableCell><Badge variant="outline" className="text-[10px]">{{ ic_account: "Verrechnung", ic_balance: "IC-Saldo", ic_pl: "IC-ER", capital: "Kapital" }[e.type] || e.type}</Badge></TableCell>
                      <TableCell className="font-mono text-sm">{e.account}</TableCell><TableCell className="text-sm">{e.company}</TableCell><TableCell className="text-sm text-slate-600">{e.text}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmt(e.amount)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="overflow-x-auto border rounded-lg">
            <Table data-testid="holding-consolidated-table">
              <TableHeader><TableRow>
                <TableHead>Konto</TableHead>
                {report.companies.map((c) => <TableHead key={c.id} className="text-right">{c.name}</TableHead>)}
                <TableHead className="text-right">Eliminierung</TableHead><TableHead className="text-right">Konzern</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {SECTIONS.map(([sec, label]) => {
                  const rows = report.rows.filter((r) => r.section === sec);
                  if (!rows.length) return null;
                  return [
                    <TableRow key={`h-${sec}`} className="bg-slate-50"><TableCell colSpan={report.companies.length + 3} className="text-xs font-semibold uppercase tracking-wide text-slate-500 py-1.5">{label}</TableCell></TableRow>,
                    ...rows.map((row) => (
                      <TableRow key={row.account_id} className={row.eliminated ? "opacity-50" : ""} data-testid={`holding-row-${row.account_id}`}>
                        <TableCell>
                          <span className={`font-mono ${row.eliminated ? "line-through" : ""}`}>{row.account_id}</span> <span className="text-slate-500 text-xs">{row.account_name}</span>
                          {row.eliminated && <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-700">eliminiert</Badge>}
                        </TableCell>
                        {report.companies.map((c) => <TableCell key={c.id} className="text-right font-mono tabular-nums text-sm">{row.balances[c.id] ? fmt(row.balances[c.id]) : "—"}</TableCell>)}
                        <TableCell className={`text-right font-mono tabular-nums text-sm ${row.elimination ? "text-amber-700" : "text-slate-300"}`}>{row.elimination ? fmt(row.elimination) : "—"}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm font-semibold">{fmt(row.total)}</TableCell>
                      </TableRow>
                    )),
                  ];
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Card>
  );
};
